<template>
  <div>
    <b-container v-if="isValid" v-test="'results valid'">
      <div class="bg-white rounded-lg p-3">
        <h5><b-icon-check-circle-fill variant="success" /> File uploaded</h5>
        <p>Your file was uploaded successfully and is now being processed.</p>
        <b-button @click="doReset" variant="success" class="mt-4">Upload another file</b-button>
      </div>
    </b-container>
    <b-container v-else v-test="'results invalid'">
      <div class="bg-white rounded-lg p-3">
        <h5><b-icon-exclamation-diamond-fill variant="danger" /> Could not upload file</h5>
        <p>Unfortunately there were errors found in the file you uploaded. Please return to the upload section to try again.</p>
        <p>We have listed below the headers <span v-if="rows.length > 0">and the first {{ rows.length }} rows </span>of your file with the errors we have detected.</p>
        <b-table-simple striped responsive sticky-header class="resultsTable" v-if="csvFile">
          <b-thead>
            <b-tr>
              <b-th v-for="(header, index) in headers" :variant="header.variant" :sticky-column="index === 0" :key="index">
                <span>
                  <span v-if="header.error" v-infinity-tooltip="header.error" :title="header.error"><b-icon-exclamation-triangle-fill variant="danger"></b-icon-exclamation-triangle-fill></span>
                  {{ header.value }}
                </span>
              </b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(row, rowIndex) in rows" :key="rowIndex">
              <b-td v-for="(column, columnIndex) in row" :variant="column.variant" :sticky-column="columnIndex === 0" :key="columnIndex">
                <span>
                  <span v-if="column.error" v-infinity-tooltip="column.error" :title="column.error"><b-icon-exclamation-triangle-fill variant="danger"></b-icon-exclamation-triangle-fill></span>
                  {{ column.value }}
                </span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>
      <div class="d-flex justify-content-center mt-3">
        <b-button @click="doReset" variant="primary"><b-icon-arrow-clockwise /> Try again with a different file</b-button>
      </div>
    </b-container>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import FileValidation from '@/models/FileValidation'
import CsvFile from '@/models/csvFile'
import {
  BIconArrowClockwise,
  BIconExclamationDiamondFill,
  BIconExclamationTriangleFill,
  BIconCheckCircleFill,
  ButtonPlugin,
  TablePlugin,
  TableSimplePlugin
} from 'bootstrap-vue'

Vue.use(TablePlugin)
Vue.use(TableSimplePlugin)
Vue.use(ButtonPlugin)

type ResultRow = {
  value?: string | null;
  variant?: string | null;
  error?: string | null;
}

@Component({
  components: {
    BIconExclamationDiamondFill,
    BIconExclamationTriangleFill,
    BIconArrowClockwise,
    BIconCheckCircleFill
  }
})
export default class Result extends Vue {
  @Prop({ default: null }) results?: FileValidation | null
  @Prop({ default: null }) csvFile?: CsvFile | null

  get isValid (): boolean {
    const { results } = this

    if (results instanceof FileValidation) {
      return typeof results.getUrl() === 'string'
    }

    return false
  }

  doReset () {
    this.$store.dispatch('reset')
  }

  /**
   * Maps a row of csv data to combine with any error messages as well as any formatting to apply to that cell
  **/
  getRowDataWithErrors (index: number, row: Array<string>): Array<ResultRow> {
    const results = this.results instanceof FileValidation ? this.results.getValidationResults() : []
    const headers = this.csvFile instanceof CsvFile ? this.csvFile.getHeaders() : []

    // add an extra column to show the row number
    return [`${index}`, ...row].map(
      (value, columnIndex) => {
        const data: ResultRow = {
          value: columnIndex === 0 ? `${index < 0 ? '' : index + 1}` : value,
          variant: index < 0 || columnIndex === 0 ? 'secondary' : null
        }
        if (Array.isArray(results[index])) {
          const errors = results[index].find(
            ({ field }) => {
              return field === (columnIndex === 0 ? '' : headers[columnIndex - 1])
            }
          )

          if (errors) {
            data.error = errors.message
          }
        }

        return data
      }
    )
  }

  get headers (): Array<ResultRow> {
    const headers = this.csvFile instanceof CsvFile ? this.csvFile.getHeaders() : []

    return this.getRowDataWithErrors(-1, headers)
  }

  get rows (): Array<Array<ResultRow>> {
    const rows = this.csvFile instanceof CsvFile ? this.csvFile.getRows() : []

    return rows.map(
      (row, rowIndex) => {
        return this.getRowDataWithErrors(rowIndex, row)
      }
    )
  }
}
</script>

<style scoped>
  table td > span, table th > span {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .resultsTable thead th, .resultsTable tbody th {
    vertical-align: top;
  }

  table td > span span, table th > span span {
    padding-right: 5px;
  }
</style>
